import type { LocalStorageKey } from '@/services/storage/types';
import type { KeyValueStore } from '../storage/StorageService';
import type { ILogger } from '../log/types';
import type { IAuthSessionStore, StoredSessionData } from './types';

export class AuthSessionStore implements IAuthSessionStore {
  private readonly SESSION_KEY: LocalStorageKey = 'sessionData';
  private readonly USER_KEY: LocalStorageKey = 'userId';

  private readonly kv: KeyValueStore<LocalStorageKey>;
  private readonly logger?: ILogger;

  constructor(kv: KeyValueStore<LocalStorageKey>, logger?: ILogger) {
    this.kv = kv;
    this.logger = logger;
  }

  /** Save full token bundle + userId to storage. */
  saveTokens(tokens: StoredSessionData) {
    this.kv.setJSON(this.SESSION_KEY, { ...tokens });
    if (tokens.userId) this.kv.setString(this.USER_KEY, tokens.userId);

    this.logger?.info('Token bundle saved', {
      userId: tokens.userId ?? null,
      expiresAt: tokens.expiresAt ?? null,
      hasAccessToken: Boolean(tokens.accessToken),
      hasRefreshToken: Boolean(tokens.refreshToken),
      hasIdToken: Boolean(tokens.idToken),
      authUserType: tokens.authUserType ?? null,
      authRole: tokens.authRole ?? null,
      email: tokens.email ?? null,
    });
  }

  /** Load token bundle + userId from storage. */
  loadTokens(): StoredSessionData {
    const session = this.kv.getJSON<StoredSessionData>(this.SESSION_KEY);

    const userId = this.kv.getString(this.USER_KEY);

    const res: StoredSessionData = {
      accessToken: session?.accessToken ?? null,
      refreshToken: session?.refreshToken ?? null,
      idToken: session?.idToken ?? null,
      expiresAt: session?.expiresAt ?? null,
      authUserType: session?.authUserType ?? null,
      authRole: session?.authRole ?? null,
      userId,
      email: session?.email ?? null,
    };

    this.logger?.debug('Token bundle loaded', {
      userId: res.userId,
      hasAccessToken: Boolean(res.accessToken),
      hasRefreshToken: Boolean(res.refreshToken),
      hasIdToken: Boolean(res.idToken),
      expiresAt: res.expiresAt,
      authUserType: res.authUserType,
      authRole: res.authRole,
      email: res.email,
    });

    return res;
  }

  /** Check expiry status of current tokens. */
  isExpired(): boolean {
    const session = this.kv.getJSON<{ expiresAt: number }>(this.SESSION_KEY);
    const expired = !session ? true : Date.now() >= session.expiresAt;

    this.logger?.trace('Checked token expiration', {
      expiresAt: session?.expiresAt ?? null,
      now: Date.now(),
      expired,
    });

    return expired;
  }

  /** Clear all auth-related data. */
  clear() {
    this.kv.remove(this.SESSION_KEY);
    this.kv.remove(this.USER_KEY);
    this.logger?.info('Auth session cleared', { cleared: [this.SESSION_KEY, this.USER_KEY] });
  }
}
